import React, { FC, useCallback, useMemo } from "react";
import { SymbolPreview as SymbolPreviewUI } from "@evergis/ui";
import { ClusterSymbolPreview, SymbolByType, SGisSymbol } from "@evergis/react";
import { ClusterSymbolDc } from "@evergis/api";

import { images } from "../../../modules/SymbolLibrary/images";
import { useSymbolSettingsContext } from "../../../modules/Style/context/styleSettingsContext";
import { getOffsetParameterValue } from "../../../evergis/core/classification/classifyOffset";
import { useAnchorPoint } from "../hooks/useAnchorPoint";
import { useSegmentStyle } from "../../../modules/Style/hooks/useSegmentStyle";
import { getExtractedSymbol } from "../../../utils/style";
import { getParameterValue } from "../../../evergis/core/classification/getFromClassified";
import {
  ClassifiedOffset,
  isClusterSymbol,
  isH3GridSymbol,
  isSimpleOffset,
  isSymbolWithOffset,
  Offset,
  ParameterValue,
  SimpleOffset,
  StyleSymbolType,
  SvgPointSymbol,
} from "../../../interfaces/style";

interface SymbolPreviewProps {
  symbol?: StyleSymbolType;
  className?: string;
  onChange: (offset: Partial<StyleSymbolType>) => void;
}

const revertOffset = ([x, y]: SimpleOffset): [number, number] => [-x, -y];

const updateOffset = (currentOffset: Offset, offset: SimpleOffset): Offset => {
  const newOffset = revertOffset(offset);

  if (isSimpleOffset(currentOffset)) {
    return newOffset;
  }

  return currentOffset.map(
    (classifiedOffset, index) => ({
      ...classifiedOffset,
      defaultValue: newOffset[index],
      values: classifiedOffset.values.map(value => {
        return {
          ...value,
          value: classifiedOffset.defaultValue
            ? (value.value / classifiedOffset.defaultValue) * newOffset[index]
            : newOffset[index],
        };
      }),
    }),
    // TODO Should be something like mapTuple
  ) as ClassifiedOffset;
};

export const SymbolPreview: FC<SymbolPreviewProps> = ({ symbol, className, onChange }) => {
  const segmentStyle = useSegmentStyle();
  const currentSymbol = symbol || segmentStyle?.symbol;
  const extracted = getExtractedSymbol(currentSymbol, true);
  const { previewSize, previewRenderSize, symbolInstance } = useSymbolSettingsContext();

  const symbolDefaultOffset =
    extracted && isSymbolWithOffset(extracted) ? getOffsetParameterValue(extracted.offset) : undefined;

  const handleOffsetChange = useCallback(
    (symbolOffset: SimpleOffset) => {
      if (extracted && isSymbolWithOffset(extracted)) {
        onChange({
          offset: updateOffset(extracted.offset, symbolOffset),
        });
      }
    },
    [extracted, onChange],
  );

  const [anchorPoint, setAnchorPoint] = useAnchorPoint(
    symbolDefaultOffset && revertOffset(symbolDefaultOffset),
    handleOffsetChange,
  );

  const renderSymbol = useMemo(() => {
    if (isH3GridSymbol(extracted)) {
      return <img src={images.defaultH3Symbol} alt="" />;
    }

    if (isClusterSymbol(currentSymbol)) {
      return <ClusterSymbolPreview symbol={currentSymbol as ClusterSymbolDc} />;
    }

    return (
      <SymbolByType
        symbol={symbolInstance as SGisSymbol}
        size={previewRenderSize}
        type={extracted.type}
        svg={getParameterValue((extracted as SvgPointSymbol).data as ParameterValue) as string}
      />
    );
  }, [currentSymbol, extracted, previewRenderSize, symbolInstance]);

  return (
    <SymbolPreviewUI
      image={renderSymbol}
      anchorPoint={anchorPoint}
      onAnchorPointChange={setAnchorPoint}
      size={previewSize}
      className={className}
    />
  );
};
